/* eslint-disable no-magic-numbers */
import React, { FC, useEffect, useState } from 'react';
import clsx from 'clsx';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  TooltipItem,
  ChartOptions
} from 'chart.js';
import { useIntl } from 'react-intl';

import { CHART_COLORS } from '@futrli/colors';
// import { formatCurrency, formatDecimal, WrappedComponentProps } from '@futrli/localisation';

import styles from './bar-chart.scss';

interface Props {
  ariaLabel?: string;
  legend?: 'top' | 'bottom' | 'left' | 'right';
  title?: string;
  labels: (string | string[])[];
  datasets: { label?: string; data: number[]; colors?: string | string[]; barPercentage?: number; stack?: string }[];
  testId?: string;
  dateFormatter?: (date: string, value?: string) => string;
  currencyFormatter?: (value: number) => string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  customGridOptions?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  customLegendOptions?: any;
  hideTicks?: boolean;
  aspectRatio?: number;
  isStacked?: boolean;
  otherOptions?: ChartOptions<'bar'>;
}

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);
ChartJS.defaults.color = '#7C879D';
ChartJS.defaults.font = {
  family: 'SageUI',
  size: 10,
  weight: '500'
};
ChartJS.defaults.plugins.tooltip.backgroundColor = 'rgb(92, 101, 120)';

export const UIBarChart: FC<Props> = ({
  ariaLabel,
  legend,
  title,
  labels,
  datasets,
  testId,
  customGridOptions,
  customLegendOptions,
  dateFormatter,
  currencyFormatter,
  hideTicks,
  aspectRatio,
  isStacked,
  otherOptions = {}
}: Props) => {
  const [gridStep, setGridStep] = useState(0);
  const intl = useIntl();

  useEffect(() => {
    let maxValue = 0;
    datasets.forEach(e => {
      const maxSet = Math.round(
        e.data.reduce((acc, cur) => (Math.abs(cur) > Math.abs(acc) ? Math.abs(cur) : Math.abs(acc)), 0)
      );
      maxValue = Math.abs(maxValue) < Math.abs(maxSet) ? Math.abs(maxSet) : Math.abs(maxValue);
    });
    if (!maxValue) {
      return;
    }
    let step = Math.pow(10, `${maxValue}`.length - 1);
    if (maxValue / step > 5) {
      step *= 2;
    } else if (maxValue / step < 3) {
      step /= 2;
    }
    setGridStep(step);
  }, [datasets]);

  const options = {
    responsive: true,
    devicePixelRatio: 4,
    maintainAspectRatio: aspectRatio ? true : false,
    aspectRatio: aspectRatio || undefined,
    plugins: {
      legend: {
        display: !!legend,
        position: legend || 'top',
        ...customLegendOptions
      },
      title: {
        display: !!title,
        text: title || ''
      },
      tooltip: {
        callbacks: {
          title: dateFormatter
            ? (tooltipItems: TooltipItem<'bar'>[]) =>
                dateFormatter(tooltipItems[0].label, tooltipItems[0].formattedValue)
            : undefined,
          label: (ctx: TooltipItem<'bar'>) =>
            currencyFormatter ? currencyFormatter(ctx.raw as number) : intl.formatNumber(ctx.raw as number)
        }
      },
      datalabels: {
        display: false
      }
    },
    scales: {
      x: {
        grid: {
          display: false,
          drawBorder: false
        },
        ticks: { display: !hideTicks },
        stacked: isStacked
      },
      y: {
        beginAtZero: true,
        grid: {
          drawBorder: false,
          drawTicks: false,
          color: '#DFE3EC',
          lineWidth: (tick: { index: number; tick: { value: number } }) => (tick?.tick?.value === 0 ? 2 : 1),
          ...customGridOptions
        },
        ticks: {
          stepSize: gridStep,
          callback: (val: string | number): string => `  ${intl.formatNumber(+val)}`
        },
        stacked: isStacked
      }
    },
    ...otherOptions
  };

  const barData = {
    labels: labels.map(l => (typeof l === 'string' && l.length > 30 ? `${l.substring(0, 27)}...` : l)),
    datasets: datasets.map(ds => ({
      label: ds.label || '',
      data: ds.data,
      backgroundColor: ds.colors || CHART_COLORS.POSITIVE.PRIMARY,
      barPercentage: ds.barPercentage || 0.8,
      stack: ds.stack
    }))
  };

  return (
    <div className={clsx(styles.chartWrapper)} data-testid={testId || 'bar-chart'}>
      <Bar
        options={options}
        data={barData}
        aria-label={
          ariaLabel ||
          intl.formatMessage({
            id: 'predict.chart.bar_chart.label',
            defaultMessage: 'Bar chart'
          })
        }
      />
    </div>
  );
};
